







































import Vue from 'vue';
import { propOfTypeArrayOf, capitalizeText } from '@/services/utils';
import TimelineSegment, {
  TimelineSegmentModel, TimelineSegmentModelType,
} from './TimelineSegment.vue';

// En px. SEGMENT_GAP y GAP_CELL_WIDTH tienen que coincidir con las clases
// `space-x-2` y `w-8` del template.
const MIN_SEGMENT_WIDTH = 34;
const SEGMENT_GAP = 8;
const GAP_CELL_WIDTH = 32;

const MIN_CELLS = 7;
const FALLBACK_CELLS = 15;
const HEAD_SEGMENTS = 2;
const TAIL_SEGMENTS = 2;
const MAX_TOOLTIP_LINES = 6;

type SegmentCell = {
  kind: 'segment'
  index: number
  segment: TimelineSegmentModel
};

type GapCell = {
  kind: 'gap'
  from: number
  to: number
  state: TimelineSegmentModelType
};

export type TimelineCell = SegmentCell | GapCell;

type SimpleResizeObserver = {
  observe(target: Element): void
  disconnect(): void
};

type ResizeObserverCtor = new (callback: () => void) => SimpleResizeObserver;

export default Vue.extend({
  components: {
    TimelineSegment,
  },
  props: {
    segments: propOfTypeArrayOf<TimelineSegmentModel>(),
  },
  data() {
    return {
      segmentHovered: null as number | null,
      containerWidth: 0,
      resizeObserver: null as SimpleResizeObserver | null,
    };
  },
  mounted() {
    this.measure();
    const Observer = (window as any).ResizeObserver as ResizeObserverCtor | undefined;
    if (!Observer) return;
    this.resizeObserver = new Observer(() => this.measure());
    this.resizeObserver.observe(this.$refs.row as Element);
  },
  beforeDestroy() {
    if (this.resizeObserver === null) return;
    this.resizeObserver.disconnect();
    this.resizeObserver = null;
  },
  methods: {
    measure() {
      const row = this.$refs.row as HTMLElement | undefined;
      if (row) this.containerWidth = row.clientWidth;
    },
    selectSegment(index: null | number) {
      if (
        index !== null
          && this.checkIfSegmentIsPast(index)
      ) {
        this.segmentHovered = index;

        this.$logEvent('seguimiento', { accion_seguimiento: 'hover_linea_de_tiempo' });
      } else {
        this.segmentHovered = null;
      }
    },
    checkIfSegmentIsPast(index: number): boolean {
      return this.segments[index]?.futurePresentOrPast === 'past';
    },
    buildCells(windowSize: number): TimelineCell[] {
      const total = this.segments.length;
      const visible = new Set<number>();
      for (let i = 0; i < Math.min(HEAD_SEGMENTS, total); i += 1) visible.add(i);
      for (let i = Math.max(0, total - TAIL_SEGMENTS); i < total; i += 1) visible.add(i);

      const size = Math.max(1, Math.min(windowSize, total));
      const start = Math.max(
        0,
        Math.min(this.currentIndex - Math.floor((size - 1) / 2), total - size),
      );
      for (let i = start; i < start + size; i += 1) visible.add(i);

      const cells: TimelineCell[] = [];
      let index = 0;
      while (index < total) {
        if (visible.has(index)) {
          cells.push({ kind: 'segment', index, segment: this.segments[index] });
          index += 1;
        } else {
          let end = index;
          while (end < total && !visible.has(end)) end += 1;
          if (end - index === 1) {
            cells.push({ kind: 'segment', index, segment: this.segments[index] });
          } else {
            cells.push({
              kind: 'gap', from: index, to: end - 1, state: this.gapState(index, end - 1),
            });
          }
          index = end;
        }
      }
      return cells;
    },
    gapState(from: number, to: number): TimelineSegmentModelType {
      const range = this.segments.slice(from, to + 1);
      if (range.some((segment) => segment.futurePresentOrPast === 'present')) return 'present';
      if (range.every((segment) => segment.futurePresentOrPast === 'future')) return 'future';
      return 'past';
    },
    gapColorClass(cell: GapCell): string {
      if (cell.state === 'present') return 'bg-accent-300';
      if (cell.state === 'past') return 'bg-accent-100';
      return 'bg-gray-300';
    },
    gapTooltip(cell: GapCell) {
      const lines: string[] = [];
      for (let i = cell.from; i <= cell.to && lines.length < MAX_TOOLTIP_LINES; i += 1) {
        const line = this.segmentLine(i);
        if (line) lines.push(line);
      }
      const hidden = (cell.to - cell.from + 1) - lines.length;
      if (hidden > 0) lines.push(`… y ${hidden} más`);
      return {
        content: lines.join('\n'),
        html: false,
        placement: 'top',
        classes: 'timeline-gap-tooltip',
      };
    },
    segmentLine(index: number): string {
      const segment = this.segments[index];
      if (segment.caption) return segment.caption;
      const direccion = segment.direccion
        ? segment.direccion.alias || segment.direccion.direccion
        : '';
      const stringDireccion = direccion
        ? ` a ${capitalizeText(direccion).split(',', 3).join(',')}`
        : '';
      return `${capitalizeText(segment.rowType)}${stringDireccion}`;
    },
  },
  computed: {
    cellCount(): number {
      if (!this.containerWidth) return FALLBACK_CELLS;
      const available = this.containerWidth - 2 * (GAP_CELL_WIDTH + SEGMENT_GAP);
      const fit = Math.floor((available + SEGMENT_GAP) / (MIN_SEGMENT_WIDTH + SEGMENT_GAP));
      return Math.max(MIN_CELLS, fit + 2);
    },
    currentIndex(): number {
      const present = this.segments.findIndex(
        (segment) => segment.futurePresentOrPast === 'present',
      );
      if (present !== -1) return present;
      let lastPast = 0;
      this.segments.forEach((segment, index) => {
        if (segment.futurePresentOrPast === 'past') lastPast = index;
      });
      return lastPast;
    },
    cells(): TimelineCell[] {
      const total = this.segments.length;
      if (total === 0) return [];
      if (total <= this.cellCount) {
        return this.segments.map((segment, index) => ({
          kind: 'segment' as const, index, segment,
        }));
      }
      let best = this.buildCells(1);
      for (let size = 2; size <= total; size += 1) {
        const candidate = this.buildCells(size);
        if (candidate.length > this.cellCount) break;
        best = candidate;
      }
      return best;
    },
    hoveredCaption(): string {
      if (this.segmentHovered === null) return '';
      return this.segments[this.segmentHovered]?.caption || '';
    },
  },
});
